import { ApiClient } from 'lib/api/ApiClient.types';
import {
  convertCustomFieldFiltersToHttp,
  FieldNameTypeMapping
} from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import { convertCoreFieldFiltersToHttp } from 'v5/core/core-fields/core-fields-filters.http-converters';
import { mergeWorkspaceMembershipScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeWorkspaceScopingIntoHttpFilters';
import {
  buildFilterConverter,
  convertEnumFilterToApi,
  convertEnumFilterToArrayApi
} from 'v5/platform/filters/filterConverters';
import { WithServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { Resource } from '../../../__types__';
import {
  type JoinWorkspaceResponse,
  type RemoveLinkResponse,
  ResourceApi,
  ResourceQuery
} from './resource.api';
import {
  resourceBatchSearchResponse$,
  resourceContactSearchListResponse$
} from './resource.api.http.schema';

// Map frontend sort fields to backend field names for resources
const mapResourceSortFieldToApi = (field: string): string => {
  const fieldMap: Record<string, string> = {
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    full_name: 'full_name',
    role: 'role',
    availability: 'availability'
    // Add more fields as needed based on Resource model
  };
  return fieldMap[field] ?? 'created_at';
};

// Convert sorter to API format
const convertResourceSorterToApi = (sorter: {
  field: string;
  order: 'asc' | 'desc';
}) => ({
  field: mapResourceSortFieldToApi(sorter.field),
  direction: sorter.order.toUpperCase() as 'ASC' | 'DESC'
});

// Convert additional resource filters (not core fields) to API format
const convertAdditionalResourceFiltersToApi = buildFilterConverter({
  id: filter => (filter ? convertEnumFilterToApi(filter) : null),
  resource_type_model: filter =>
    filter ? convertEnumFilterToApi(filter) : null,
  enterprise_workspace_id: filter =>
    filter ? convertEnumFilterToArrayApi(filter) : null
});

export function getResourceListRequest(
  scopedQuery: ResourceQuery,
  fieldMappings: FieldNameTypeMapping
): Record<string, unknown> {
  const additionalFilters = convertAdditionalResourceFiltersToApi(
    scopedQuery.filters
  );
  if (
    scopedQuery.workspaceId !== null &&
    scopedQuery.filters.enterprise_workspace_id
  ) {
    throw new Error(
      'Workspace-scoped resource search cannot include filters.enterprise_workspace_id; membership is the browsing workspace only'
    );
  }
  const filters = {
    // Convert core field filters
    ...convertCoreFieldFiltersToHttp(scopedQuery.filters),
    ...additionalFilters,
    // Convert custom field filters using the field type mappings
    ...convertCustomFieldFiltersToHttp(
      fieldMappings,
      scopedQuery.filters,
      scopedQuery.workspaceId
    ),
    // Workspace lists: assigned membership only. Global (null) is org-talents / pre-enterprise.
    ...mergeWorkspaceMembershipScopingIntoHttpFilters(scopedQuery),
    ...(scopedQuery.available && {
      available: {
        start_date: scopedQuery.available.startDate,
        end_date: scopedQuery.available.endDate,
        status_ids: scopedQuery.available.statusIds
      }
    }),
    ...(scopedQuery.booked && {
      availability: {
        status: scopedQuery.booked.statusIds,
        from_date: scopedQuery.booked.startDate,
        to_date: scopedQuery.booked.endDate
      }
    })
  };

  return {
    query: scopedQuery.search,
    filters,
    p: scopedQuery.page.page + 1, // API uses 1-based pagination
    count: scopedQuery.page.pageSize,
    // opt-in: without it the backend answers as before
    ...(scopedQuery.includePinsPreview && { includePinsPreview: true }),
    // Note: 'archived' is at root level, not in filters, per backend API requirements
    archived: scopedQuery.filters?.archived?.value ? true : false,
    // Sorting support
    ...(scopedQuery.sort
      ? {
          order_by: {
            ...convertResourceSorterToApi(scopedQuery.sort),
            // CF sort lens (SearchRouter → customFieldWorkspaceId): which overlay
            // value to order by when the sort field is a custom column. null =
            // global values only (org talents); set = that workspace's overlay
            // with global fallback. Not contact membership filtering.
            enterprise_workspace_id: scopedQuery.workspaceId
          }
        }
      : {})
  };
}

export function createResourceApi(
  config: { client: ApiClient } & WithServices<
    [WithMetastructureMappingService]
  >
): ResourceApi {
  const { client, services } = config;
  return {
    getList: async (query: ResourceQuery, signal) => {
      // Get field type mappings for custom fields
      const fieldMappings =
        await services.metastructureMappingService.getAllFieldNameTypeMappings();

      const requestData = getResourceListRequest(query, fieldMappings);

      const response = await client.post('/search/contacts', {
        data: requestData,
        token: true,
        signal
      });

      return parseWithDecoratedError(
        resourceContactSearchListResponse$,
        response
      );
    },

    getItem: async id => {
      return client.get<Resource>(`/contacts/${id}`, { token: true });
    },

    getItems: async ids => {
      if (ids.length === 0) {
        return [];
      }

      const response = await client.post('/batch/contacts/search', {
        data: { ids },
        token: true
      });

      return parseWithDecoratedError(resourceBatchSearchResponse$, response);
    },

    updateItem: async (id, data) => {
      return client.put<Resource>(`/contacts/${id}`, {
        data,
        token: true
      });
    },

    createItem: async data => {
      return client.post<Resource>('/contacts', {
        data,
        token: true
      });
    },

    assignResourcesToWorkspaces: async request => {
      await client.post('/batch/contacts/assign-workspaces', {
        data: request,
        token: true
      });
    },

    joinWorkspace: async request =>
      client.post<JoinWorkspaceResponse>('/batch/contacts/join-workspace', {
        data: request,
        token: true
      }),

    removeFromOrganisation: async request =>
      client.post<RemoveLinkResponse>(
        '/batch/contacts/remove-from-organisation',
        { data: request, token: true }
      ),

    removeFromAgent: async request =>
      client.post<RemoveLinkResponse>('/batch/contacts/remove-from-agent', {
        data: request,
        token: true
      }),

    bulkArchive: async request => {
      await client.del('/batch/contacts', {
        data: { ids: request.ids },
        token: true
      });
    },

    bulkUnarchive: async request => {
      await Promise.all(
        request.ids.map(id =>
          client.put(`/contacts/${id}`, {
            data: { archived: false },
            token: true
          })
        )
      );
    },

    createPin: async request => {
      await client.post(`/contacts/${request.resourceId}/associations/pins`, {
        data: {
          url: request.accessUrl,
          pin_type: 'file',
          file_id: request.fileId
        },
        token: true
      });
    }
  };
}
